import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Image as ImageIcon, QrCode, SearchX } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useCompanyMembers } from "@/hooks/useCompanyMembers";
import { displayNameFor } from "@/lib/avatarColor";
import { useSpareParts } from "@/hooks/useSpareParts";
import { useAllStorageBins, formatBinAddress } from "@/hooks/useStorageBins";
import { groupPartTypes, partTypeIdFor } from "@/lib/partTypes";
import { getCategoryFallbackImage } from "@/lib/categoryFallbackImage";
import { SparePartLabel } from "@/components/SparePartLabel";
import { SparePartLabelPrintMenu } from "@/components/labels/SparePartLabelPrintMenu";
import { buildSparePartPrintJobs, resolveSparePartLabelSize, useLabelPrinter, useLabelPrintingSettings } from "@/labels";
import { useResalePricePct } from "@/hooks/useResalePricePct";
import { useEprelSearch } from "@/hooks/useEprelSearch";
import { Button } from "@/components/ui/button";
import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { useSparePartMovements } from "@/hooks/useSparePartMovements";
import { isTakenOut, partStockState } from "@/lib/partStatus";
import { PartStatusBlock } from "@/components/inventory/PartStatusBlock";
import { lastOutBinId } from "@/lib/partTakeOut";
import { PartTakeOutActions } from "@/components/inventory/PartTakeOutActions";
import { PartMovementsCard } from "@/components/inventory/PartMovementsCard";


function formatHarvestedAt(iso: string): string {
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const time = d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${date} · ${time}`;
  } catch {
    return iso;
  }
}


export default function SparePartDetail() {
  const navigate = useNavigate();
  const { token } = useParams();
  const { data: members = [] } = useCompanyMembers();

  const { data: spareParts = [], isPending: partsPending } = useSpareParts();
  const { data: bins = [], isLoading: binsLoading } = useAllStorageBins();

  const unit = useMemo(
    () => spareParts.find((p) => p.serial_no === token || p.qr_token === token),
    [spareParts, token],
  );

  const resalePct = useResalePricePct(unit?.company_id);

  const partType = useMemo(() => {
    if (!unit) return null;
    const id = partTypeIdFor(unit);
    return groupPartTypes(spareParts).find((p) => p.id === id) || null;
  }, [spareParts, unit]);

  const bin = useMemo(
    () => (unit?.bin_id ? bins.find((b) => b.id === unit.bin_id) : null),
    [bins, unit],
  );

  const { settings: labelPrintingSettings } = useLabelPrintingSettings();
  const { zebraPrint } = useLabelPrinter();
  const handleZebraPrintPart = async () => {
    if (!unit) return;
    const size = resolveSparePartLabelSize(labelPrintingSettings.defaultLabelSize);
    const jobs = buildSparePartPrintJobs(
      [
        {
          id: unit.id,
          name: unit.name || "Spare part",
          serial_no: unit.serial_no,
          qr_token: unit.qr_token,
          brand: unit.brand,
          model_number: unit.model_number,
          harvested_at: unit.harvested_at,
        },
      ],
      { size },
    );
    await zebraPrint(jobs);
  };

  const { data: appliance } = useQuery({
    queryKey: ["appliance_for_unit", unit?.appliance_id],
    enabled: !!unit?.appliance_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("appliances")
        .select("id, name, brand, model_number, serial_number, category, image_url, eprel_data, eprel_reference, specifications")
        .eq("id", unit!.appliance_id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { t } = useTranslation("inventory");
  const donorWoId = (unit as { harvest_work_order_id?: string | null } | undefined)?.harvest_work_order_id ?? null;
  const { data: workOrder } = useQuery({
    queryKey: ["harvest_wo_for_appliance", unit?.appliance_id, donorWoId],
    enabled: !!unit?.appliance_id || !!donorWoId,
    queryFn: async () => {
      // FIX-286: the part's own work order; appliance lookup only for legacy rows.
      if (donorWoId) {
        const { data, error } = await supabase
          .from("work_orders").select("id, completed_at, status").eq("id", donorWoId).maybeSingle();
        if (error) return null; // not readable → no link, no error
        return data;
      }
      const { data, error } = await supabase
        .from("work_orders")
        .select("id, completed_at, status")
        .eq("appliance_id", unit!.appliance_id!)
        .eq("type", "harvest")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) return null; // not readable → no link, no error
      return data;
    },
  });

  // Compatibility: distinct appliances that produced units of this part type
  const compatibleApplianceIds = useMemo(() => {
    if (!partType) return [] as string[];
    return Array.from(
      new Set(
        partType.units
          .map((u) => u.appliance_id)
          .filter((id): id is string => !!id),
      ),
    );
  }, [partType]);

  const { data: compatibleAppliances = [] } = useQuery({
    queryKey: ["compatible_appliances_for_part", compatibleApplianceIds.join(",")],
    enabled: compatibleApplianceIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("appliances")
        .select("id, brand, model_number, category, image_url")
        .in("id", compatibleApplianceIds);
      if (error) throw error;
      return data || [];
    },
  });

  const compatibilityRows = useMemo(() => {
    const seen = new Map<
      string,
      { id: string; brand: string | null; model_number: string | null; category: string | null; image_url: string | null }
    >();
    for (const a of compatibleAppliances) {
      const key = `${(a.brand ?? "").toLowerCase()}|${(a.model_number ?? "").toLowerCase()}`;
      if (!seen.has(key)) seen.set(key, a);
    }
    return Array.from(seen.values());
  }, [compatibleAppliances]);


  const { allowed: canManageCompany } = useCanManageCompany();
  const { data: movements = [], isLoading: movementsLoading } = useSparePartMovements(unit?.id);
  const takenOut = isTakenOut(unit?.status);
  const binAddressFor = (binId: string | null) => {
    const b = binId ? bins.find((x) => x.id === binId) : null;
    return b ? formatBinAddress(b.aisle, b.bay, b.level, b.shelf, b.bin) : null;
  };
  const lastOutBin = useMemo(() => {
    const id = lastOutBinId(movements);
    const address = binAddressFor(id);
    return id && address ? { id, address } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movements, bins]);

  const binAddress = bin
    ? formatBinAddress(bin.aisle, bin.bay, bin.level, bin.shelf, bin.bin)
    : null;

  const harvestedByLabel = useMemo(() => {
    if (!unit) return "—";
    const member = members.find((m) => m.id === unit.user_id);
    return member ? displayNameFor(member.name, member.email) : unit.user_id.slice(0, 8);
  }, [unit, members]);


  const { searchByModelAndBrand, searchByEprelReference } = useEprelSearch();
  const searchByModelAndBrandRef = useRef(searchByModelAndBrand);
  const searchByEprelReferenceRef = useRef(searchByEprelReference);
  searchByModelAndBrandRef.current = searchByModelAndBrand;
  searchByEprelReferenceRef.current = searchByEprelReference;
  const [eprelMarketDate, setEprelMarketDate] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setEprelMarketDate(null);
    if (!appliance) return;
    const brand = appliance.brand || appliance.name?.split(" ")[0];
    const modelNumber = appliance.model_number;
    const category = appliance.category;
    if (!brand || brand === "Unknown" || !modelNumber || !category) return;
    (async () => {
      try {
        const res = appliance.eprel_reference
          ? await searchByEprelReferenceRef.current(appliance.eprel_reference, category)
          : await searchByModelAndBrandRef.current(modelNumber, brand, category);
        if (cancelled) return;
        const arr = (res?.data as { onMarketStartDate?: number[] } | undefined)?.onMarketStartDate;
        if (Array.isArray(arr) && arr.length >= 3) {
          setEprelMarketDate(
            `${arr[0]}-${String(arr[1]).padStart(2, "0")}-${String(arr[2]).padStart(2, "0")}`,
          );
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliance?.id, appliance?.brand, appliance?.name, appliance?.model_number, appliance?.category, appliance?.eprel_reference]);

  const introductionDate = useMemo(() => {
    const eprel = (appliance?.eprel_data ?? null) as Record<string, unknown> | null;
    if (eprel) {
      const arr = eprel.onMarketStartDate as unknown;
      if (Array.isArray(arr) && arr.length >= 3) {
        return `${arr[0]}-${String(arr[1]).padStart(2, "0")}-${String(arr[2]).padStart(2, "0")}`;
      }
      const raw =
        (typeof arr === "string" ? arr : undefined) ||
        (eprel.firstPublicationDate as string | undefined) ||
        (eprel.publishedOnDate as string | undefined) ||
        null;
      if (raw) {
        const d = new Date(raw);
        if (!Number.isNaN(d.getTime())) {
          return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
        }
        return String(raw);
      }
    }
    return eprelMarketDate;
  }, [appliance?.eprel_data, eprelMarketDate]);

  const manufacturingDate = useMemo(() => {
    const specs = (appliance?.specifications ?? []) as Array<{ label?: string; value?: string }>;
    if (!Array.isArray(specs)) return null;
    const matchers = [
      /^manufacturing date$/i,
      /^model placed on the union market from$/i,
      /^fabrikationsdatum$/i,
      /manufactur.*date/i,
    ];
    for (const m of matchers) {
      const hit = specs.find((s) => s?.label && m.test(s.label));
      if (hit?.value) return hit.value;
    }
    return null;
  }, [appliance?.specifications]);




  const shortWoId = workOrder?.id ? workOrder.id.slice(0, 8).toUpperCase() : null;
  const fixIdLabel = unit?.serial_no || unit?.qr_token || "—";

  const workOrderHref = workOrder?.id && unit?.appliance_id
    ? `/appliance/${unit.appliance_id}/workflow/harvesting?wo=${workOrder.id}&workOrderId=${workOrder.id}`
    : null;

  // FIX-298: status block data
  const stockState = partStockState(unit?.status, unit?.extraction_status, unit?.bin_id);
  const statusLoading = !unit || binsLoading || movementsLoading;
  const lastOut = useMemo(
    () => movements.filter((m) => m.type === "out").sort((a, b) => b.performed_at.localeCompare(a.performed_at))[0] ?? null,
    [movements],
  );
  const nameOf = (id: string) => {
    const m = members.find((x) => x.id === id);
    return m ? displayNameFor(m.name, m.email) : id.slice(0, 8);
  };
  const reuseValue = unit?.estimated_value != null ? (Number(unit.estimated_value) * resalePct).toFixed(2) : null;
  const photoUrl = unit?.extraction_photo_url || null;
  const [photoOpen, setPhotoOpen] = useState(false);

  // Compatibility only when it adds a model other than the donor's own.
  const donorModelKey = (unit?.model_number ?? appliance?.model_number ?? "").trim().toLowerCase();
  const showCompatibility = compatibilityRows.some(
    (a) => (a.model_number ?? "").trim() !== "" && (a.model_number ?? "").trim().toLowerCase() !== donorModelKey,
  );

  const donor = unit as unknown as {
    donor_legacy_missing?: boolean; donor_appliance_category?: string | null;
    donor_serial_number?: string | null; brand?: string | null; model_number?: string | null;
    origin_company_id?: string | null; company_id?: string | null;
  } | undefined;
  const donorSerial = donor?.donor_serial_number || appliance?.serial_number || null;
  const donorLine = [donor?.donor_appliance_category ?? appliance?.category, donor?.brand ?? appliance?.brand, donor?.model_number ?? appliance?.model_number]
    .filter(Boolean)
    .join(" · ");

  const cardTitle = "text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-4";
  const notKnown = <span className="text-muted-foreground">{t("part.notKnownYet")}</span>;

  if (partsPending) {
    return (
      <div className="min-h-screen bg-background pb-24" aria-busy="true">
        <div className="px-4 sm:px-6 pt-8 pb-6 max-w-6xl mx-auto">
          <Skeleton className="h-3 w-56 mb-6" />
          <div className="flex items-start gap-4 mb-5">
            <Skeleton className="w-[72px] h-[72px] rounded-lg shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-8 w-56" />
              <Skeleton className="h-4 w-72 max-w-full" />
            </div>
          </div>
          <div className="mb-4">
            <PartStatusBlock loading state="notInBin" />
          </div>
          <div className="flex flex-col gap-4 lg:grid lg:grid-cols-5 lg:items-start">
            <div className="flex flex-col gap-4 lg:col-span-3">
              <Skeleton className="h-40 rounded-xl" />
              <Skeleton className="h-64 rounded-xl" />
            </div>
            <div className="flex flex-col gap-4 lg:col-span-2">
              <Skeleton className="h-56 rounded-xl" />
              <Skeleton className="h-32 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <div className="px-4 sm:px-6 pt-16 pb-6 max-w-xl mx-auto text-center">
          <div className="w-14 h-14 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
            <SearchX className="w-7 h-7 text-muted-foreground" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground">{t("part.notFoundTitle")}</h1>
          <p className="font-mono text-sm text-foreground mt-2 break-all">{token}</p>
          <p className="text-sm text-muted-foreground mt-2">{t("part.notFoundHint")}</p>
          <Button className="mt-6 min-h-12 px-6 w-full sm:w-auto" onClick={() => navigate("/inventory?tab=parts")}>
            {t("part.backToInventory")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-4 sm:px-6 pt-8 pb-6 max-w-6xl mx-auto print:hidden">
        {/* Breadcrumb */}
        <nav className="text-xs text-muted-foreground mb-6 break-all">
          <button onClick={() => navigate("/inventory?tab=parts")} className="hover:text-foreground">
            Inventory
          </button>
          <span className="mx-1.5">/</span>
          {partType ? (
            <button
              onClick={() => navigate(`/inventory/parts/${partType.id}`)}
              className="hover:text-foreground"
            >
              {partType.name}
            </button>
          ) : (
            <span>Part type</span>
          )}
          <span className="mx-1.5">/</span>
          <span className="text-foreground font-mono">{token}</span>
        </nav>

        {/* Header */}
        <div className="flex items-start gap-4 mb-5">
          {photoUrl ? (
            <button
              type="button"
              onClick={() => setPhotoOpen(true)}
              className="w-[72px] h-[72px] rounded-lg overflow-hidden border border-border bg-muted shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t("part.openPhoto")}
            >
              <img src={photoUrl} alt={t("part.photoAlt", { name: unit?.name || "" })} className="w-full h-full object-cover" />
            </button>
          ) : (
            <div className="w-[72px] h-[72px] rounded-lg border border-dashed border-border bg-muted shrink-0 flex flex-col items-center justify-center gap-1 text-muted-foreground">
              <ImageIcon className="w-5 h-5" aria-hidden="true" />
              <span className="text-[10px]">{t("part.noPhoto")}</span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              {t("part.caption")}
            </div>
            <h1 className="text-foreground font-semibold break-words" style={{ fontSize: 28, lineHeight: 1.15 }}>
              {unit?.name || "Spare part"}
            </h1>
            <div className="mt-1 text-sm text-muted-foreground flex flex-wrap items-center gap-x-2">
              <span>
                {t("part.partNo")} <span className="font-mono text-foreground">{fixIdLabel}</span>
              </span>
              {reuseValue && (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="text-foreground">{t("part.value", { value: reuseValue })}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Status block */}
        <div className="mb-4">
          <PartStatusBlock
            loading={statusLoading}
            state={stockState}
            bin={bin && binAddress ? { id: bin.id, address: binAddress, description: bin.description } : null}
            takenOut={
              takenOut
                ? {
                    reason: unit!.status,
                    when: lastOut ? formatHarvestedAt(lastOut.performed_at) : null,
                    by: lastOut ? nameOf(lastOut.performed_by) : null,
                    note: lastOut?.note ?? null,
                  }
                : null
            }
            actions={
              unit && stockState !== "damaged" ? (
                <PartTakeOutActions
                  partId={unit.id}
                  partName={unit.name || "Spare part"}
                  status={unit.status}
                  extractionStatus={unit.extraction_status}
                  canUndo={canManageCompany}
                  lastOutBin={lastOutBin}
                  buttonClassName="w-full sm:w-auto min-h-12 sm:min-h-10 px-6"
                />
              ) : undefined
            }
          />
        </div>

        {/* Body: on narrow screens one column, ordered via `order`; columns become `contents`. */}
        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-5 lg:items-start">
          {/* Left 60%: Movements, Label */}
          <div className="contents lg:flex lg:flex-col lg:gap-4 lg:col-span-3">
            <div className="order-2 lg:order-none">
              <PartMovementsCard movements={movements} binAddressFor={binAddressFor} isLoading={movementsLoading} />
            </div>

            {/* Label */}
            {unit && (
              <div className="order-4 lg:order-none bg-card rounded-xl border border-border p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium inline-flex items-center gap-2">
                    <QrCode className="w-3.5 h-3.5" />
                    {t("part.label")}
                  </div>
                  <SparePartLabelPrintMenu
                    namespace="inventory"
                    mode="single"
                    iconOnly
                    ariaLabel={t("part.printLabel")}
                    onPrintBrowser={() => window.print()}
                    onPrintZebra={handleZebraPrintPart}
                  />
                </div>
                <SparePartLabel
                  qrValue={unit.qr_token || unit.serial_no || ""}
                  partName={unit.name || "Spare part"}
                  partId={unit.serial_no || unit.qr_token || "—"}
                  sourceBrand={appliance?.brand ?? null}
                  sourceModel={appliance?.model_number ?? null}
                  harvestedAt={unit.harvested_at}
                />
              </div>
            )}
          </div>

          {/* Right 40%: Where it came from, Part data, Compatibility */}
          <div className="contents lg:flex lg:flex-col lg:gap-4 lg:col-span-2">
            <div className="order-1 lg:order-none bg-card rounded-xl border border-border p-5 sm:p-6">
              <div className={cardTitle}>{t("part.whereFrom")}</div>
              <dl className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-y-1 sm:gap-y-3 gap-x-4 text-sm">
                <dt className="text-muted-foreground">{t("part.donor")}</dt>
                <dd className="text-foreground mb-2 sm:mb-0">
                  {!donor ? (
                    "—"
                  ) : donor.donor_legacy_missing ? (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-muted text-muted-foreground">
                      {t("donor.legacy")}
                    </span>
                  ) : (
                    <span className="block">
                      {donor.origin_company_id && donor.origin_company_id !== donor.company_id && (
                        <span className="block text-xs text-muted-foreground">{t("donor.otherCompany")}</span>
                      )}
                      {appliance ? (
                        <button
                          onClick={() => navigate(`/appliance/${appliance.id}`)}
                          className="text-left font-medium text-primary hover:underline"
                        >
                          {donorLine || appliance.name}
                        </button>
                      ) : (
                        <span className="font-medium">{donorLine || "—"}</span>
                      )}
                      <span className="block text-xs text-muted-foreground">
                        {t("part.serial")} <span className="font-mono">{donorSerial ?? t("part.notKnownYet")}</span>
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {t("part.manufacturingDate")}: {manufacturingDate ?? introductionDate ?? t("part.notKnownYet")}
                      </span>
                    </span>
                  )}
                </dd>
                <dt className="text-muted-foreground">{t("part.dateHarvested")}</dt>
                <dd className="text-foreground mb-2 sm:mb-0">
                  {unit ? formatHarvestedAt(unit.harvested_at) : "—"}
                </dd>
                <dt className="text-muted-foreground">{t("part.harvestedBy")}</dt>
                <dd className="text-foreground mb-2 sm:mb-0">{harvestedByLabel}</dd>
                {workOrderHref && shortWoId && (
                  <>
                    <dt className="text-muted-foreground">{t("part.workOrder")}</dt>
                    <dd>
                      <button
                        onClick={() => navigate(workOrderHref)}
                        className="font-mono text-primary hover:underline text-left"
                      >
                        FIXID-{shortWoId}
                      </button>
                    </dd>
                  </>
                )}
              </dl>
            </div>

            <div className="order-3 lg:order-none bg-card rounded-xl border border-border p-5 sm:p-6">
              <div className={cardTitle}>{t("part.partData")}</div>
              <dl className="grid grid-cols-[130px_1fr] gap-y-2 gap-x-4 text-sm">
                <dt className="text-muted-foreground">{t("part.partType")}</dt>
                <dd className="text-foreground">{partType?.name || unit?.parts_category_name || notKnown}</dd>
                <dt className="text-muted-foreground">{t("part.oemNumber")}</dt>
                <dd className={partType?.oem_part_number ? "font-mono text-foreground" : ""}>{partType?.oem_part_number || notKnown}</dd>
                <dt className="text-muted-foreground">{t("part.manufacturer")}</dt>
                <dd className="text-foreground">{unit?.manufacturer || notKnown}</dd>
              </dl>
            </div>

            {showCompatibility && (
              <div className="order-5 lg:order-none bg-card rounded-xl border border-border p-5 sm:p-6">
                <div className={cardTitle}>{t("part.compatibility")}</div>
                <ul className="flex flex-col gap-2">
                  {compatibilityRows.map((a) => (
                    <li key={a.id}>
                      <button
                        onClick={() => navigate(`/appliance/${a.id}`)}
                        className="flex items-center gap-3 w-full text-left hover:bg-muted/40 -mx-2 px-2 py-2 rounded-lg transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-foreground text-sm">
                            {a.brand || t("part.unknownBrand")}
                          </div>
                          <div className="font-mono text-xs text-muted-foreground">
                            {a.model_number || "—"}
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={photoOpen} onOpenChange={setPhotoOpen}>
        <DialogContent className="max-w-3xl p-2">
          <DialogTitle className="sr-only">{unit?.name || "Spare part"}</DialogTitle>
          {photoUrl && (
            <img src={photoUrl} alt={t("part.photoAlt", { name: unit?.name || "" })} className="w-full h-auto max-h-[80vh] object-contain rounded-md" />
          )}
        </DialogContent>
      </Dialog>

      {/* Print-only single label */}
      {unit && (
        <div className="hidden print:block p-6">
          <SparePartLabel
            qrValue={unit.qr_token || unit.serial_no || ""}
            partName={unit.name || "Spare part"}
            partId={unit.serial_no || unit.qr_token || "—"}
            sourceBrand={appliance?.brand ?? null}
            sourceModel={appliance?.model_number ?? null}
            harvestedAt={unit.harvested_at}
          />
        </div>
      )}

      <style>{`
        @media print {
          @page { margin: 10mm; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}
